import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { debounceTime, ReplaySubject, Subject, takeUntil, tap } from 'rxjs';
import { LayoutService } from '../../../core/services/layout/layout';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { PAGINATION } from '../../../core/constants/constants';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { AsyncPipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

export interface OptionSelect {
  value: string | number;
  label: string;
}

@Component({
  selector: 'app-search-select',
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatOption,
    NgxMatSelectSearchModule,
    MatError,
    AsyncPipe,
  ],
  templateUrl: './search-select.html',
  styleUrl: './search-select.css',
})
export class SearchSelect {
  @Input() dataList: OptionSelect[] = [];
  @Input() placeholder: string = 'selecione una opción';
  @Input() searchLabel: string = 'Buscar';
  @Input() noResults: string = 'sin resultados';
  @Output() onSelectOption: EventEmitter<OptionSelect> = new EventEmitter();
  @Output() onSearchOption: EventEmitter<string | number> = new EventEmitter();
  @Input() initialValue: OptionSelect | null = null;
  @Input() label: string = '';
  @Input() readonly: boolean = false;
  @Input() delayTime: number = PAGINATION.delay_time;
  private firtsRender: boolean = true;

  /** control for the selected option */
  public optionCtrl: FormControl = new FormControl();

  /** control for the MatSelect filter keyword */
  public optionFilterCtrl: FormControl = new UntypedFormControl();

  /** list of options filtered by search keyword */
  public filteredOptions: ReplaySubject<OptionSelect[]> = new ReplaySubject<OptionSelect[]>(1);

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();

  selectOption(data: any) {
    this.onSelectOption.emit(data);
  }

  constructor(private layoutService: LayoutService) {}

  ngOnInit(): void {
    // load the initial option list
    this.optionCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((data) => {
      this.selectOption(data);
    });

    // listen for search field value changes
    this.optionFilterCtrl.valueChanges
      .pipe(
        tap(() => {
          this.filterOptions();
          this.layoutService.setLoading(true);
        }),
        takeUntil(this._onDestroy),
        debounceTime(this.delayTime),
      )
      .subscribe((data) => {
        const MIN_SEARCH_LENGTH = 5;
        if (typeof data === 'string' && data.length > MIN_SEARCH_LENGTH) {
          this.onSearchOption.emit(data);
        } else {
          this.layoutService.setLoading(false);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes['dataList'] !== 'undefined') {
      this.filteredOptions.next(changes['dataList'].currentValue);
    }
    if (
      typeof changes['initialValue'] !== 'undefined' &&
      this.firtsRender &&
      changes['initialValue'].currentValue
    ) {
      this.firtsRender = false;
      const optionIndex = this.dataList.findIndex(
        (el: OptionSelect) => el.value === changes['initialValue'].currentValue.value,
      );
      this.optionCtrl.setValue(this.dataList[optionIndex]);
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected filterOptions() {
    if (!this.dataList) {
      return;
    }
    // get the search keyword
    let search: string = this.optionFilterCtrl.value;
    if (!search) {
      this.filteredOptions.next(this.dataList.slice());
      return;
    } else {
      search = search.toUpperCase();
    }
    // filter the options
    this.filteredOptions.next(
      this.dataList.filter((item: OptionSelect) => item.label.toUpperCase().indexOf(search) > -1),
    );
  }
}
